import { useState, useMemo, useCallback } from 'react';
import { TrainingSession, CompetitionMatch } from '../../types';
import { useTrainingJournal } from '../../context/useTrainingJournal';

interface UseSessionsListProps {
  sessions?: TrainingSession[] | null;
  searchQuery: string;
  limit: number;
  showAllButton: boolean;
}

export function useSessionsList({
  sessions: propSessions,
  searchQuery,
  limit,
  showAllButton
}: UseSessionsListProps) {
  const { sessions: contextSessions, deleteSession: contextDeleteSession } = useTrainingJournal();
  const [selectedSession, setSelectedSession] = useState<TrainingSession | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState<TrainingSession | null>(null);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [showingAll, setShowingAll] = useState(false);

  // Use prop sessions if provided, otherwise use context sessions
  // Memoize to keep reference stable for downstream hooks
  const sessions = useMemo(
    () => propSessions || contextSessions || [],
    [propSessions, contextSessions]
  );

  // Enhanced filter sessions based on search query - searches ALL relevant fields including competition data
  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return sessions;
    
    return sessions.filter((session: TrainingSession) => {
      const searchLower = searchQuery.toLowerCase();
      
      // Basic session info
      const basicFieldsMatch = (
        session.title?.toLowerCase().includes(searchLower) ||
        session.notes?.toLowerCase().includes(searchLower) ||
        session.location?.toLowerCase().includes(searchLower) ||
        session.professor?.toLowerCase().includes(searchLower) ||
        
        // Reflection fields
        session.learned?.toLowerCase().includes(searchLower) ||
        session.improvements?.toLowerCase().includes(searchLower) ||
        session.successes?.toLowerCase().includes(searchLower) ||
        
        // Techniques array
        session.techniques?.some((technique: string) =>
          technique.toLowerCase().includes(searchLower)
        ) ||
        
        // Date search (format: YYYY-MM-DD)
        session.date?.includes(searchLower) ||
        
        // Belt rank and stripe info
        session.beltRank?.toLowerCase().includes(searchLower) ||
        session.stripeCount?.toLowerCase().includes(searchLower)
      );

      // Competition-specific fields
      const competitionFieldsMatch = (
        // Competition finish/result
        session.competitionFinish?.toLowerCase().includes(searchLower) ||
        
        // Competition weight classes
        session.competitionWeightClasses?.toLowerCase().includes(searchLower) ||
        
        // Competition total matches and wins (convert to string for searching)
        session.competitionTotalMatches?.toString().includes(searchLower) ||
        session.competitionTotalWins?.toString().includes(searchLower)
      );

      // Search within competition matches JSONB data
      let competitionMatchesMatch = false;
      if (session.competitionMatches && Array.isArray(session.competitionMatches)) {
        competitionMatchesMatch = session.competitionMatches.some((match: unknown) => {
          const typedMatch = match as CompetitionMatch;
          return (
            // Style (gi/nogi)
            typedMatch.gi_nogi?.toLowerCase().includes(searchLower) ||

            // Division
            typedMatch.division?.toLowerCase().includes(searchLower) ||

            // Weight class
            typedMatch.weight_class?.toLowerCase().includes(searchLower) ||

            // Result (win/loss)
            typedMatch.result?.toLowerCase().includes(searchLower) ||

            // Method (submission/points/dq)
            typedMatch.method?.toLowerCase().includes(searchLower) ||

            // Video URL if present
            typedMatch.video_url?.toLowerCase().includes(searchLower)
          );
        });
      }

      return basicFieldsMatch || competitionFieldsMatch || competitionMatchesMatch;
    });
  }, [sessions, searchQuery]);

  // Check if we're currently searching
  const isSearching = searchQuery.trim().length > 0;

  // Get displayed sessions based on search state and limit
  const displayedSessions = useMemo(() => {
    // If searching, always show ALL filtered results regardless of limit
    if (isSearching) {
      return filteredSessions;
    }
    
    // If not searching, apply normal limit/showAll logic
    if (!showAllButton || showingAll) {
      return filteredSessions;
    }
    
    return filteredSessions.slice(0, limit);
  }, [filteredSessions, limit, showAllButton, showingAll, isSearching]);

  const handleViewDetails = useCallback((session: TrainingSession) => {
    setSelectedSession(session);
    setIsDetailsOpen(true);
  }, []);

  const handleEdit = useCallback((session: TrainingSession) => {
    setSessionToEdit(session);
    setIsEditFormOpen(true);
  }, []);

  const handleShowAll = useCallback(() => {
    setShowingAll(true);
  }, []);

  const handleShowLess = useCallback(() => {
    setShowingAll(false);
  }, []);

  return {
    displayedSessions,
    filteredSessions,
    selectedSession,
    isDetailsOpen,
    setIsDetailsOpen,
    sessionToEdit,
    isEditFormOpen,
    setIsEditFormOpen,
    handleViewDetails,
    handleEdit,
    handleShowAll,
    handleShowLess,
    showingAll,
    deleteSession: contextDeleteSession,
    isSearching // Export this so components can react to search state
  };
}
